/**
 * The active experiment's public faces.
 *
 * Standalone, a demo lives at `/<slug>/<key>` — the shareable form, which the
 * static host rewrites to the entry file (firebase.json) the way Vite's SPA
 * fallback does. `?exp=<key>` is also accepted, and is what tooling and the
 * platform's iframe use. Embedded in the platform, the demo keeps `?exp=`:
 * a sandboxed frame has an opaque origin, and Chromium only lets such a
 * document rewrite its query and fragment; the host owns the visible URL and
 * mirrors the experiment it is told about.
 *
 * The frame usually finishes booting before the platform page has hydrated
 * and attached its listener, so a boot-time announcement alone is lost. The
 * platform therefore posts `{ type: 'experiment-request' }` once it is
 * listening (and again whenever the frame reloads); demos answer through
 * `onExperimentRequest`.
 */

import { isEmbedded } from './embed';

const PARAM = 'exp';

// A key is a code identifier — never a file name (`index.html`) or a nested path.
const SEGMENT = /^[A-Za-z0-9][A-Za-z0-9-]*$/;

/** `/…/<slug>/` when `pathname` is the demo root or one segment below it, else null. */
function demoBasePath(slug: string, pathname: string): string | null {
  const escaped = slug.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = new RegExp(`^(.*/${escaped}/)[^/]*$`).exec(pathname);
  return match ? match[1]! : null;
}

/** Read `?exp=` from `href` (defaults to the current page). Empty → null. */
export function readExperimentParam(href: string = window.location.href): string | null {
  try {
    const value = new URL(href).searchParams.get(PARAM);
    return value === null || value === '' ? null : value;
  } catch {
    return null;
  }
}

/** Read the `<key>` of `/<slug>/<key>` from `href` (defaults to the current page). */
export function readExperimentSegment(
  slug: string,
  href: string = window.location.href,
): string | null {
  try {
    const { pathname } = new URL(href);
    const base = demoBasePath(slug, pathname);
    if (base === null) return null;
    const segment = pathname.slice(base.length);
    return SEGMENT.test(segment) ? segment : null;
  } catch {
    return null;
  }
}

/**
 * The experiment the URL asks for. `?exp=` is an explicit request (tooling,
 * the platform's iframe, or a URL edited in place before `reload()`), so it
 * wins over the `/<slug>/<key>` segment, which is the persisted state; the
 * next `writeExperimentUrl` folds the request back into the path.
 */
export function readRequestedExperiment(
  slug: string,
  href: string = window.location.href,
): string | null {
  return readExperimentParam(href) ?? readExperimentSegment(slug, href);
}

/**
 * Reflect `key` in the page URL in place (no new history entry): as
 * `/<slug>/<key>` when standalone and served under `/<slug>/`, otherwise as
 * `?exp=<key>` (embedded frames cannot change their path; see above).
 */
export function writeExperimentUrl(slug: string, key: string): void {
  const url = new URL(window.location.href);
  const base = isEmbedded() ? null : demoBasePath(slug, url.pathname);
  if (base === null) {
    url.searchParams.set(PARAM, key);
  } else {
    url.pathname = `${base}${key}`;
    url.searchParams.delete(PARAM);
  }
  history.replaceState(null, '', url.toString());
}

/**
 * Tell the parent window which experiment is showing. No-op when the demo is
 * the top-level page. The target origin must be `'*'`: the platform sandboxes
 * the iframe with `allow-scripts` only, so the demo runs under an opaque origin
 * and cannot know the host's.
 */
export function notifyHostExperiment(key: string): void {
  if (!isEmbedded()) return;
  window.parent.postMessage({ type: 'experiment', value: key }, '*');
}

/** Run `handler` each time the host asks which experiment is showing. Returns an unsubscribe. */
export function onExperimentRequest(handler: () => void): () => void {
  const listener = (e: MessageEvent): void => {
    const data = e.data as { type?: unknown } | null;
    if (!data || typeof data !== 'object') return;
    if (data.type !== 'experiment-request') return;
    handler();
  };
  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
}
